import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import queryString from 'query-string';

import {renamePropertyValue} from 'common/report/util';

export function expandToAnomalyDetails(anomalyAlert) {
  return anomalyAlert.anomaly.anomalyDetails.map((anomalyDetail) => {
    const anomaly = {...anomalyAlert.anomaly, anomalyDetail};
    delete anomaly.anomalyDetails;
    const anomalyDetailAlert = cloneDeep({...anomalyAlert, anomaly});
    delete anomalyDetailAlert.readAtMs;
    delete anomalyDetailAlert.sentFeedbackAtMs;
    return anomalyDetailAlert;
  });
}

// Expand a contour primary into a list of contour details in the format needed for mp-smart-hub-alert
export function expandToContourDetails(contourAlert) {
  const contourDetails = orderBy(contourAlert.contour.contourDetails, (cd) => cd.score, `desc`);
  return contourDetails.map((contourDetail) => {
    const contour = {...contourAlert.contour, contourDetail};
    delete contour.contourDetails;
    const contourDetailAlert = cloneDeep({...contourAlert, contour});
    delete contourDetailAlert.readAtMs;
    delete contourDetailAlert.sentFeedbackAtMs;
    return contourDetailAlert;
  });
}

export function getAlertDirection(alert) {
  const {anomaly, contour} = alert;
  if (anomaly) {
    if (anomaly.anomalyDetail) {
      return anomaly.anomalyDetail.direction;
    } else {
      return anomaly.anomalyDetails.some((d) => d.direction === `POSITIVE`) ? `POSITIVE` : `NEGATIVE`;
    }
  } else if (contour) {
    const contour = alert.contour;
    if (contour.contourDetail) {
      return contour.contourDetail.conversionRate >= contour.baseConversionRate ? `POSITIVE` : `NEGATIVE`;
    } else {
      const hasPositive = contour.contourDetails.some((d) => d.conversionRate >= contour.baseConversionRate);
      return hasPositive ? `POSITIVE` : `NEGATIVE`;
    }
  }
}

export function getAlertIdentifier(alert) {
  const identifier = {alertId: alert.alertId};
  const {anomaly, contour} = alert;
  if (anomaly && anomaly.anomalyDetail) {
    identifier.anomalyDetailId = anomaly.anomalyDetail.anomalyDetailId;
  } else if (alert.contour && contour.contourDetail) {
    identifier.contourDetailId = contour.contourDetail.contourDetailId;
  }
  return identifier;
}

export function isMatchingAlert(alert1, alert2) {
  return isEqual(getAlertIdentifier(alert1), getAlertIdentifier(alert2));
}

export function getSimilarAlertIdentifier(alert) {
  const identifier = {alertId: alert.alertId};
  const {anomaly, contour} = alert;
  if (anomaly) {
    if (anomaly.anomalyDetail) {
      identifier.anomalyDetailId = anomaly.anomalyDetail.anomalyDetailId;
    } else if (anomaly.anomalyDetails.length === 1) {
      identifier.anomalyDetailId = anomaly.anomalyDetails[0].anomalyDetailId;
    }
  } else if (contour) {
    if (contour.contourDetail) {
      identifier.contourDetailId = contour.contourDetail.contourDetailId;
    } else if (contour.contourDetails.length === 1) {
      identifier.contourDetailId = contour.contourDetails[0].contourDetailId;
    }
  }
  return identifier;
}

export function isSimilarAlert(alert1, alert2) {
  return isEqual(getSimilarAlertIdentifier(alert1), getSimilarAlertIdentifier(alert2));
}

export function getSentFeedbackAtMs(alert) {
  const {anomaly, contour} = alert;
  if (anomaly && anomaly.anomalyDetail) {
    return anomaly.anomalyDetail.sentFeedbackAtMs;
  } else if (contour && contour.contourDetail) {
    return contour.contourDetail.sentFeedbackAtMs;
  } else {
    return alert.sentFeedbackAtMs;
  }
}

export function setSentFeedbackAtMs(alert, ms) {
  const {anomaly, contour} = alert;
  if (anomaly && anomaly.anomalyDetail) {
    anomaly.anomalyDetail.sentFeedbackAtMs = ms;
  } else if (contour && contour.contourDetail) {
    contour.contourDetail.sentFeedbackAtMs = ms;
  } else {
    alert.sentFeedbackAtMs = ms;
  }
  return alert;
}

export function takeFirstPresentProperty(obj, properties) {
  const propertyIndex = properties.findIndex((property) => obj.hasOwnProperty(property));
  return propertyIndex >= 0 ? obj[properties[propertyIndex]] : null;
}

const CONTOUR_SEGMENT_VALUE_FIELDS = [`stringValue`, `boolValue`, `numValue`];

export function getRawContourSegment({contourDetail}) {
  return takeFirstPresentProperty(contourDetail, CONTOUR_SEGMENT_VALUE_FIELDS).toString();
}

export function getContourSegment({contourDetail, property}) {
  return renamePropertyValue(getRawContourSegment({contourDetail}), property);
}

export function getCommonApiQueryParams() {
  const params = {};

  if (localStorage.getItem(`mp_smart_hub_use_mock_response`)) {
    params.use_mock_response = 1; // eslint-disable-line camelcase
  }

  if (localStorage.getItem(`mp_smart_hub_use_staging`)) {
    params.use_staging_smarthub = 1; // eslint-disable-line camelcase
  }

  return params;
}

export function withCommonApiQueryParams(url) {
  const qs = queryString.stringify(getCommonApiQueryParams());
  return qs ? `${url}?${qs}` : url;
}

// TODO(mack): Create app_api client rather than individual functions
export function getAlertUrl({projectId, alertId}) {
  return withCommonApiQueryParams(`/api/app/projects/${projectId}/smart-hub/alerts/${alertId}`);
}

export function getAlertDetailUrl({projectId, alertId, detailId}) {
  return withCommonApiQueryParams(`/api/app/projects/${projectId}/smart-hub/alerts/${alertId}/details/${detailId}`);
}

export function reportType(alert) {
  const {anomaly, contour} = alert;
  if (anomaly) {
    if (anomaly.insightsQuery) {
      return `Insights`;
    } else if (anomaly.funnelsQuery) {
      return `Funnels`;
    } else if (anomaly.retentionQuery) {
      return `Retention`;
    }
  } else if (contour) {
    if (contour.insightsDetails) {
      return `Insights`;
    } else if (contour.funnelsDetails) {
      return `Funnels`;
    } else if (contour.retentionDetails) {
      return `Retention`;
    }
  }
}

export function injectContourBaseConversionRate(alert, baseConversionRate) {
  alert.contour.baseConversionRate = baseConversionRate;
  return alert;
}

export function injectContourConversionRate(alert, conversionRate) {
  alert.contour.contourDetail.conversionRate = conversionRate;
  return alert;
}

export function replaceContourSegment(alert, segment) {
  const {contourDetail} = alert.contour;
  CONTOUR_SEGMENT_VALUE_FIELDS.forEach((field) => {
    if (contourDetail.hasOwnProperty(field)) {
      delete contourDetail[field];
    }
  });
  contourDetail.stringValue = segment.toString();
  return alert;
}

export function injectAnomalyValue(alert, value) {
  alert.anomaly.anomalyDetail.valueAtTimestamp = value;
  return alert;
}
